import React, { useState, useEffect } from "react";
import { database } from "../../firebaseConfig";
import { ref, set, get, push } from "firebase/database";
import "./TeamSettings.css";

// Helper function to convert numbers to German words
const numberToGermanWords = (num) => {
  const ones = ['', 'eins', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun'];
  const tens = ['', '', 'zwanzig', 'dreißig', 'vierzig', 'fünfzig', 'sechzig', 'siebzig', 'achtzig', 'neunzig'];
  const teens = ['zehn', 'elf', 'zwölf', 'dreizehn', 'vierzehn', 'fünfzehn', 'sechzehn', 'siebzehn', 'achtzehn', 'neunzehn'];
  const thousands = ['', 'tausend', 'million', 'milliarde'];

  if (num === 0) return 'null';
  if (num < 0) return 'minus ' + numberToGermanWords(-num);

  let result = '';

  // Handle thousands
  if (num >= 1000) {
    const thousandsPart = Math.floor(num / 1000);
    if (thousandsPart === 1) {
      result += 'ein';
    } else {
      result += numberToGermanWords(thousandsPart);
    }
    result += 'tausend';
    num = num % 1000;
    if (num > 0) result += 'und';
  }

  // Handle hundreds
  if (num >= 100) {
    const hundredsPart = Math.floor(num / 100);
    if (hundredsPart === 1) {
      result += 'ein';
    } else {
      result += ones[hundredsPart];
    }
    result += 'hundert';
    num = num % 100;
    if (num > 0) result += 'und';
  }

  // Handle tens and ones
  if (num >= 20) {
    const tensPart = Math.floor(num / 10);
    const onesPart = num % 10;
    if (onesPart > 0) {
      result += ones[onesPart] + 'und';
    }
    result += tens[tensPart];
  } else if (num >= 10) {
    result += teens[num - 10];
  } else if (num > 0) {
    result += ones[num];
  }

  return result;
};

// Format amount with currency and German words
const formatAmount = (amount, currency = 'EUR', includeWords = true) => {
  const formattedAmount = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);

  if (!includeWords) {
    return formattedAmount;
  }

  const amountWords = numberToGermanWords(Math.floor(amount));
  const currencyWord = currency === 'EUR' ? 'Euro' : currency;
  
  return `${formattedAmount} (${amountWords}${amountWords.endsWith('s') ? '' : amountWords === 'eins' ? '' : 's'} ${currencyWord})`;
};

// Format date in German format
const formatDate = (dateValue, format = 'full') => {
  if (!dateValue) return '[Datum nicht gesetzt]';
  
  let date;
  if (typeof dateValue === 'string') {
    // Try to parse different date formats
    if (dateValue.includes('T')) {
      date = new Date(dateValue);
    } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      date = new Date(dateValue + 'T00:00:00');
    } else {
      date = new Date(dateValue);
    }
  } else if (typeof dateValue === 'number') {
    date = new Date(dateValue);
  } else {
    date = dateValue;
  }

  if (isNaN(date.getTime())) {
    return '[Ungültiges Datum]';
  }

  if (format === 'full') {
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } else if (format === 'short') {
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } else {
    return date.toLocaleDateString('de-DE');
  }
};

// Format duration (e.g., 12 months -> "12 Monate" or "1 Jahr")
const formatDuration = (value, unit = 'months') => {
  const num = parseInt(value);
  if (isNaN(num)) return '[Ungültige Dauer]';

  if (unit === 'months' || unit === 'Monate' || unit === 'month') {
    if (num === 1) return '1 Monat';
    if (num === 12) return '1 Jahr';
    if (num > 12 && num % 12 === 0) {
      const years = num / 12;
      return years === 1 ? '1 Jahr' : `${years} Jahre`;
    }
    return `${num} Monate`;
  } else if (unit === 'years' || unit === 'Jahre' || unit === 'year' || unit === 'Jahr') {
    if (num === 1) return '1 Jahr';
    return `${num} Jahre`;
  } else if (unit === 'days' || unit === 'Tage' || unit === 'day' || unit === 'Tag') {
    if (num === 1) return '1 Tag';
    return `${num} Tage`;
  } else if (unit === 'weeks' || unit === 'Wochen' || unit === 'week' || unit === 'Woche') {
    if (num === 1) return '1 Woche';
    return `${num} Wochen`;
  }

  return `${num} ${unit}`;
};

const Contracts = ({ userId }) => {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState({
    name: "",
    paragraphs: []
  });
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [editingParagraphIndex, setEditingParagraphIndex] = useState(null);
  const [newParagraphText, setNewParagraphText] = useState("");
  const [newParagraphTitle, setNewParagraphTitle] = useState("");
  const [showVariableHelp, setShowVariableHelp] = useState(false);

  // Load existing contract templates
  useEffect(() => {
    const loadTemplates = async () => {
      if (!userId) return;

      try {
        const templatesRef = ref(database, `/World/${userId}/contractTemplates`);
        const snapshot = await get(templatesRef);

        if (snapshot.exists()) {
          const templatesList = [];
          snapshot.forEach((childSnapshot) => {
            templatesList.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
          // Sort by creation date (newest first)
          templatesList.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          setTemplates(templatesList);
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading contract templates:", error);
        showNotification("Error loading contract templates", "error");
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [userId]);

  // Handle template name change
  const handleTemplateNameChange = (e) => {
    setCurrentTemplate(prev => ({
      ...prev,
      name: e.target.value
    }));
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: "" }));
    }
  };

  // Add new paragraph
  const handleAddParagraph = () => {
    if (!newParagraphTitle.trim()) {
      showNotification("Please enter paragraph title", "error");
      return;
    }
    if (!newParagraphText.trim()) {
      showNotification("Please enter paragraph text", "error");
      return;
    }

    const newParagraph = {
      id: Date.now(),
      title: newParagraphTitle.trim(),
      text: newParagraphText.trim(),
      order: currentTemplate.paragraphs.length + 1
    };

    setCurrentTemplate(prev => ({
      ...prev,
      paragraphs: [...prev.paragraphs, newParagraph]
    }));
    setNewParagraphTitle("");
    setNewParagraphText("");
    showNotification("Paragraph added", "success");
  };

  // Edit paragraph
  const handleEditParagraph = (index) => {
    setEditingParagraphIndex(index);
    const paragraph = currentTemplate.paragraphs[index];
    setNewParagraphTitle(paragraph.title || "");
    setNewParagraphText(paragraph.text || "");
  };

  // Update paragraph
  const handleUpdateParagraph = () => {
    if (!newParagraphTitle.trim()) {
      showNotification("Please enter paragraph title", "error");
      return;
    }
    if (!newParagraphText.trim()) {
      showNotification("Please enter paragraph text", "error");
      return;
    }

    const updatedParagraphs = [...currentTemplate.paragraphs];
    updatedParagraphs[editingParagraphIndex] = {
      ...updatedParagraphs[editingParagraphIndex],
      title: newParagraphTitle.trim(),
      text: newParagraphText.trim()
    };

    setCurrentTemplate(prev => ({
      ...prev,
      paragraphs: updatedParagraphs
    }));
    setNewParagraphTitle("");
    setNewParagraphText("");
    setEditingParagraphIndex(null);
    showNotification("Paragraph updated", "success");
  };

  // Delete paragraph
  const handleDeleteParagraph = (index) => {
    if (!window.confirm("Are you sure you want to delete this paragraph?")) return;

    const updatedParagraphs = currentTemplate.paragraphs.filter((_, i) => i !== index);
    // Reorder paragraphs
    updatedParagraphs.forEach((para, i) => {
      para.order = i + 1;
    });

    setCurrentTemplate(prev => ({
      ...prev,
      paragraphs: updatedParagraphs
    }));
    showNotification("Paragraph deleted", "success");
  };

  // Move paragraph up
  const handleMoveParagraphUp = (index) => {
    if (index === 0) return;

    const updatedParagraphs = [...currentTemplate.paragraphs];
    [updatedParagraphs[index - 1], updatedParagraphs[index]] = 
      [updatedParagraphs[index], updatedParagraphs[index - 1]];
    
    // Update order
    updatedParagraphs.forEach((para, i) => {
      para.order = i + 1;
    });

    setCurrentTemplate(prev => ({
      ...prev,
      paragraphs: updatedParagraphs
    }));
  };

  // Move paragraph down
  const handleMoveParagraphDown = (index) => {
    if (index === currentTemplate.paragraphs.length - 1) return;

    const updatedParagraphs = [...currentTemplate.paragraphs];
    [updatedParagraphs[index], updatedParagraphs[index + 1]] = 
      [updatedParagraphs[index + 1], updatedParagraphs[index]];
    
    // Update order
    updatedParagraphs.forEach((para, i) => {
      para.order = i + 1;
    });

    setCurrentTemplate(prev => ({
      ...prev,
      paragraphs: updatedParagraphs
    }));
  };

  // Cancel paragraph edit
  const handleCancelParagraphEdit = () => {
    setNewParagraphTitle("");
    setNewParagraphText("");
    setEditingParagraphIndex(null);
  };

  // Edit template
  const handleEditTemplate = (template) => {
    setCurrentTemplate({
      name: template.name || "",
      paragraphs: template.paragraphs ? [...template.paragraphs] : []
    });
    setEditingId(template.id);
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this contract template?")) return;

    try {
      const templateRef = ref(database, `/World/${userId}/contractTemplates/${templateId}`);
      await set(templateRef, null);
      
      setTemplates(prev => prev.filter(template => template.id !== templateId));
      showNotification("Contract template deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting contract template:", error);
      showNotification("Error deleting contract template", "error");
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!currentTemplate.name.trim()) {
      newErrors.name = "Template name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!userId) return;

    if (!validateForm()) {
      showNotification("Please fill in all required fields correctly", "error");
      return;
    }

    try {
      let templateRef;
      const templateData = {
        name: currentTemplate.name.trim(),
        paragraphs: currentTemplate.paragraphs,
        updatedAt: Date.now()
      };

      if (editingId) {
        templateRef = ref(database, `/World/${userId}/contractTemplates/${editingId}`);
        await set(templateRef, templateData);

        setTemplates(prev => prev.map(template => 
          template.id === editingId ? { ...templateData, id: editingId, createdAt: template.createdAt } : template
        ));
      } else {
        templateRef = ref(database, `/World/${userId}/contractTemplates`);
        const newTemplateRef = push(templateRef);
        await set(newTemplateRef, {
          ...templateData,
          createdAt: Date.now()
        });

        const newTemplate = {
          id: newTemplateRef.key,
          ...templateData,
          createdAt: Date.now()
        };
        setTemplates(prev => [newTemplate, ...prev]);
      }

      // Reset form
      setCurrentTemplate({
        name: "",
        paragraphs: []
      });
      setNewParagraphTitle("");
      setNewParagraphText("");
      setEditingParagraphIndex(null);
      setErrors({});
      setEditingId(null);
      showNotification(
        editingId ? "Contract template successfully updated" : "New contract template successfully created", 
        "success"
      );
    } catch (error) {
      console.error("Error saving contract template:", error);
      showNotification("Error saving contract template", "error");
    }
  };

  // Show notification
  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  // Reset form
  const handleResetForm = () => {
    setCurrentTemplate({
      name: "",
      paragraphs: []
    });
    setNewParagraphTitle("");
    setNewParagraphText("");
    setEditingParagraphIndex(null);
    setErrors({});
    setEditingId(null);
  };

  // Extract variables from text
  const extractVariables = (text) => {
    const variablePattern = /\{\{(\w+)\}\}/g;
    const variables = [];
    let match;
    const seen = new Set();
    
    while ((match = variablePattern.exec(text)) !== null) {
      const varName = match[1];
      
      // Avoid duplicates
      if (seen.has(varName)) continue;
      seen.add(varName);
      
      const varNameLower = varName.toLowerCase();
      
      let variable = {
        name: varName,
        fullMatch: match[0],
        type: 'text'
      };

      // Determine variable type based on name
      if (varNameLower.includes('amount') || varNameLower.includes('fee') || varNameLower.includes('price') || 
          varNameLower.includes('summe') || varNameLower.includes('betrag') || varNameLower.includes('kosten')) {
        variable.type = 'amount';
      } else if (varNameLower.includes('date') || varNameLower.includes('datum') || varNameLower.includes('start') || 
                 varNameLower.includes('ende') || varNameLower.includes('end') || varNameLower.includes('zeitpunkt')) {
        variable.type = 'date';
      } else if (varNameLower.includes('duration') || varNameLower.includes('dauer') || varNameLower.includes('laufzeit')) {
        variable.type = 'duration';
      }

      variables.push(variable);
    }
    return variables;
  };

  // Preview text with variables replaced (shows placeholders)
  const previewTextWithVariables = (text) => {
    const variablePattern = /\{\{(\w+)\}\}/g;
    return text.replace(variablePattern, (match, varName) => {
      const varNameLower = varName.toLowerCase();
      
      // Show type-specific placeholders
      if (varNameLower.includes('amount') || varNameLower.includes('fee') || varNameLower.includes('price') || 
          varNameLower.includes('summe') || varNameLower.includes('betrag') || varNameLower.includes('kosten')) {
        return `[${varName} - Betrag]`;
      }
      
      if (varNameLower.includes('date') || varNameLower.includes('datum') || varNameLower.includes('start') || 
          varNameLower.includes('ende') || varNameLower.includes('end') || varNameLower.includes('zeitpunkt')) {
        return `[${varName} - Datum]`;
      }
      
      if (varNameLower.includes('duration') || varNameLower.includes('dauer') || varNameLower.includes('laufzeit')) {
        return `[${varName} - Dauer]`;
      }
      
      // For other variables, show placeholder
      return `[${varName}]`;
    });
  };

  if (isLoading) {
    return (
      <div className="offer-maker__loading">
        <div className="offer-maker__spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="offer-maker__editor">
      {notification.message && (
        <div className={`offer-maker__notification offer-maker__notification--${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Templates List */}
      <div className="offer-maker__section">
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">📄</span>
          <h3 className="offer-maker__section-title">Contract Templates</h3>
        </div>

        <div className="offer-maker__team-list">
          {templates.length === 0 ? (
            <div className="offer-maker__empty-state">
              <p>No contract templates yet. Create your first template below.</p>
            </div>
          ) : (
            templates.map(template => (
              <div key={template.id} className="offer-maker__team-member-card">
                <div className="offer-maker__team-member-info">
                  <h4>{template.name}</h4>
                  <p>
                    <strong>{template.paragraphs?.length || 0}</strong> paragraph(s)
                  </p>
                  {template.paragraphs && template.paragraphs.length > 0 && (
                    <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                      <strong>Preview:</strong>
                      <div style={{ marginTop: '5px', maxHeight: '100px', overflow: 'hidden' }}>
                        <strong>{template.paragraphs[0].title || 'Untitled'}:</strong> {template.paragraphs[0].text.substring(0, 120)}
                        {template.paragraphs[0].text.length > 120 ? '...' : ''}
                      </div>
                    </div>
                  )}
                </div>
                <div className="offer-maker__team-member-actions">
                  <button 
                    className="offer-maker__edit-btn"
                    onClick={() => handleEditTemplate(template)}
                  >
                    Edit
                  </button>
                  <button 
                    className="offer-maker__delete-btn"
                    onClick={() => handleDeleteTemplate(template.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Form */}
      <div className="offer-maker__section">
        <div className="offer-maker__section-header">
          <span className="offer-maker__section-icon">{editingId ? '✏️' : '➕'}</span>
          <h3 className="offer-maker__section-title">
            {editingId ? 'Edit Contract Template' : 'Create New Contract Template'}
          </h3>
        </div>

        <div className="offer-maker__input-group">
          <label className="offer-maker__label">
            Template Name <span className="offer-maker__required">*</span>
          </label>
          <input
            type="text"
            value={currentTemplate.name}
            onChange={handleTemplateNameChange}
            className={`offer-maker__input ${errors.name ? 'offer-maker__input--error' : ''}`}
            placeholder="e.g. Service Contract Template, NDA Template"
          />
          {errors.name && <span className="offer-maker__error-message">{errors.name}</span>}
        </div>

        {/* Paragraphs Section */}
        <div className="offer-maker__section-header" style={{ marginTop: '2rem' }}>
          <span className="offer-maker__section-icon">📝</span>
          <h3 className="offer-maker__section-title">Legal Paragraphs</h3>
        </div>

        {/* Existing Paragraphs */}
        {currentTemplate.paragraphs.length > 0 && (
          <div className="offer-maker__paragraphs-list" style={{ marginBottom: '1.5rem' }}>
            {currentTemplate.paragraphs.map((paragraph, index) => (
              <div key={paragraph.id || index} className="offer-maker__paragraph-item" style={{
                border: '1px solid #eee',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                backgroundColor: '#f9f9f9'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                    <span style={{ 
                      fontWeight: 'bold', 
                      color: '#6897B7',
                      minWidth: '30px'
                    }}>
                      §{paragraph.order || index + 1}
                    </span>
                    <h4 style={{ 
                      margin: 0, 
                      fontSize: '1em', 
                      fontWeight: '600',
                      color: '#333'
                    }}>
                      {paragraph.title || 'Untitled'}
                    </h4>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button
                        type="button"
                        onClick={() => handleMoveParagraphUp(index)}
                        disabled={index === 0}
                        style={{
                          padding: '0.25rem 0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: index === 0 ? '#f5f5f5' : 'white',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '0.8em'
                        }}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveParagraphDown(index)}
                        disabled={index === currentTemplate.paragraphs.length - 1}
                        style={{
                          padding: '0.25rem 0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: index === currentTemplate.paragraphs.length - 1 ? '#f5f5f5' : 'white',
                          cursor: index === currentTemplate.paragraphs.length - 1 ? 'not-allowed' : 'pointer',
                          fontSize: '0.8em'
                        }}
                        title="Move down"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => handleEditParagraph(index)}
                      className="offer-maker__edit-btn"
                      style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem' }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteParagraph(index)}
                      className="offer-maker__delete-btn"
                      style={{ fontSize: '0.85em', padding: '0.4rem 0.8rem' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div style={{ 
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6',
                  color: '#333'
                }}>
                  {paragraph.text}
                </div>
                {extractVariables(paragraph.text).length > 0 && (
                  <div style={{ 
                    marginTop: '0.75rem', 
                    padding: '0.5rem', 
                    backgroundColor: '#e7f3ff', 
                    borderRadius: '4px',
                    fontSize: '0.85em'
                  }}>
                    <strong>Variables:</strong> {extractVariables(paragraph.text).map((v, i) => {
                      const typeLabel = v.type === 'amount' ? ' (Betrag)' : 
                                       v.type === 'date' ? ' (Datum)' : 
                                       v.type === 'duration' ? ' (Dauer)' : '';
                      return (
                        <span key={i} style={{ marginLeft: '0.5rem', color: '#6897B7' }}>
                          {v.name}{typeLabel}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Paragraph Form */}
        <div className="offer-maker__input-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label className="offer-maker__label" style={{ marginBottom: 0 }}>
              {editingParagraphIndex !== null ? 'Edit Paragraph' : 'Add New Paragraph'}
            </label>
            <button
              type="button"
              onClick={() => setShowVariableHelp(!showVariableHelp)}
              style={{
                padding: '0.25rem 0.75rem',
                fontSize: '0.85em',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {showVariableHelp ? 'Hide' : 'Show'} Variable Help
            </button>
          </div>
          
          {showVariableHelp && (
            <div style={{
              marginBottom: '1rem',
              padding: '1rem',
              backgroundColor: '#f9f9f9',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '0.9em'
            }}>
              <strong>How to use variables:</strong>
              <p style={{ marginTop: '0.5rem', marginBottom: '0.75rem', color: '#666' }}>
                Use <code>{'{{variableName}}'}</code> to create placeholders. Values will be set when creating a contract.
              </p>
              
              <div style={{ marginTop: '0.75rem' }}>
                <strong style={{ color: '#6897B7' }}>💰 Amounts:</strong>
                <ul style={{ marginTop: '0.25rem', paddingLeft: '1.5rem', marginBottom: '0.5rem' }}>
                  <li><code>{'{{setupFee}}'}</code> - Setup fee</li>
                  <li><code>{'{{monthlyFee}}'}</code> - Monthly fee</li>
                  <li><code>{'{{amount}}'}</code> - Generic amount</li>
                  <li><code>{'{{summe}}'}</code> - Sum (German)</li>
                </ul>
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <strong style={{ color: '#6897B7' }}>📅 Dates:</strong>
                <ul style={{ marginTop: '0.25rem', paddingLeft: '1.5rem', marginBottom: '0.5rem' }}>
                  <li><code>{'{{startDate}}'}</code> - Contract start date</li>
                  <li><code>{'{{endDate}}'}</code> - Contract end date</li>
                  <li><code>{'{{contractStart}}'}</code> - Contract start</li>
                  <li><code>{'{{vertragsbeginn}}'}</code> - Contract start (German)</li>
                </ul>
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <strong style={{ color: '#6897B7' }}>⏱️ Duration:</strong>
                <ul style={{ marginTop: '0.25rem', paddingLeft: '1.5rem', marginBottom: '0.5rem' }}>
                  <li><code>{'{{duration}}'}</code> - Contract duration</li>
                  <li><code>{'{{laufzeit}}'}</code> - Duration (German)</li>
                  <li><code>{'{{contractDuration}}'}</code> - Contract duration</li>
                </ul>
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <strong style={{ color: '#6897B7' }}>📝 Text:</strong>
                <ul style={{ marginTop: '0.25rem', paddingLeft: '1.5rem', marginBottom: 0 }}>
                  <li><code>{'{{customerName}}'}</code> - Customer name</li>
                  <li><code>{'{{companyName}}'}</code> - Company name</li>
                  <li><code>{'{{anyVariableName}}'}</code> - Any custom variable</li>
                </ul>
              </div>

              <p style={{ marginTop: '0.75rem', marginBottom: 0, color: '#666', fontSize: '0.85em' }}>
                <strong>Tip:</strong> Variable names containing keywords like "amount", "date", "duration" will be automatically recognized and formatted when creating contracts.
              </p>
            </div>
          )}

          <input
            type="text"
            value={newParagraphTitle}
            onChange={(e) => setNewParagraphTitle(e.target.value)}
            className="offer-maker__input"
            placeholder="Paragraph title (e.g. 'Payment Terms', 'Termination Clause')"
            style={{ marginBottom: '0.75rem' }}
          />
          <textarea
            value={newParagraphText}
            onChange={(e) => setNewParagraphText(e.target.value)}
            className="offer-maker__input offer-maker__textarea"
            placeholder={`Enter paragraph text here...\n\nExample with variables:\nDer Vertrag beginnt am {{startDate}} und hat eine Laufzeit von {{duration}}.\nDer Kunde verpflichtet sich zur Zahlung einer Setup Fee in Höhe von {{setupFee}} NETTO.`}
            rows="8"
            style={{ fontFamily: 'inherit' }}
          />
          
          {/* Preview with variables */}
          {newParagraphText && extractVariables(newParagraphText).length > 0 && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.75rem',
              backgroundColor: '#f0f8ff',
              border: '1px solid #6897B7',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}>
              <strong style={{ color: '#6897B7' }}>Preview:</strong>
              <div style={{ 
                marginTop: '0.5rem',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6',
                color: '#333'
              }}>
                {previewTextWithVariables(newParagraphText)}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            {editingParagraphIndex !== null ? (
              <>
                <button
                  type="button"
                  onClick={handleUpdateParagraph}
                  className="offer-maker__save-btn"
                  style={{ fontSize: '0.9em', padding: '0.5rem 1rem' }}
                >
                  Update Paragraph
                </button>
                <button
                  type="button"
                  onClick={handleCancelParagraphEdit}
                  className="offer-maker__cancel-btn"
                  style={{ fontSize: '0.9em', padding: '0.5rem 1rem' }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleAddParagraph}
                className="offer-maker__save-btn"
                style={{ fontSize: '0.9em', padding: '0.5rem 1rem' }}
              >
                Add Paragraph
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="offer-maker__button-group">
        {editingId && (
          <button 
            className="offer-maker__cancel-btn" 
            onClick={handleResetForm}
          >
            Cancel Edit
          </button>
        )}
        <button 
          className="offer-maker__save-btn" 
          onClick={handleSave}
          disabled={currentTemplate.paragraphs.length === 0}
        >
          {editingId ? 'Update' : 'Create'} Template
          <span className="offer-maker__save-icon">💾</span>
        </button>
      </div>
    </div>
  );
};

export default Contracts;
